import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { InventorySummaryReportService } from "services/inventory-summary-report";
import { formatVndIntegerInput, parseVndIntegerInput } from "utils";
import { getPrintTemplate } from "./components/printTemplate";

const createCommitteeMember = (role = "Ủy viên") => ({
  fullName: "",
  position: "",
  representative: "",
  role,
});

const createRow = () => ({
  itemName: "",
  itemCode: "",
  unit: "",
  unitPrice: "",
  bookQuantity: "",
  actualQuantity: "",
  surplusQuantity: "",
  shortageQuantity: "",
  goodQuantity: "",
  poorQuantity: "",
  lostQuantity: "",
});

const splitDate = (text = "") => {
  const [day = "", month = "", year = ""] = String(text).split("/");
  return {
    day: day.replace(/\D/g, "").slice(0, 2),
    month: month.replace(/\D/g, "").slice(0, 2),
    year: year.replace(/\D/g, "").slice(0, 4),
  };
};

const composeDate = (parts) => {
  if (!parts.day || !parts.month || !parts.year) return "";
  return `${String(parts.day).padStart(2, "0")}/${String(parts.month).padStart(2, "0")}/${parts.year}`;
};

const validateDate = (parts, label) => {
  const date = composeDate(parts);
  if (!date) throw new Error(`${label} chưa hợp lệ`);
  const [d, m, y] = date.split("/").map(Number);
  const maxDay = new Date(y, m, 0).getDate();
  if (m < 1 || m > 12 || y < 1900 || y > 9999 || d < 1 || d > maxDay) {
    throw new Error(`${label} không hợp lệ`);
  }
  return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
};

const parseQty = (value) => {
  const normalized = String(value || "").replace(/,/g, ".").replace(/[^\d.]/g, "");
  if (!normalized) return 0;
  const numeric = Number(normalized);
  if (!Number.isFinite(numeric) || numeric < 0) return 0;
  return Number(numeric.toFixed(3));
};

const formatQtyInput = (value) => String(value || "").replace(/,/g, ".").replace(/[^\d.]/g, "");

const formatQtyOutput = (value) => {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return "";
  if (Math.abs(number - Math.trunc(number)) < 0.000001) return `${Math.trunc(number)}`;
  return number.toFixed(3).replace(/\.0+$/, "").replace(/(\.\d*?)0+$/, "$1");
};

const roundMoney = (value) => {
  const parsed = parseVndIntegerInput(value);
  return Number.isFinite(parsed) ? Math.round(parsed) : 0;
};

const fmtMoney = (value) => new Intl.NumberFormat("vi-VN").format(Math.round(Number(value) || 0));

const calculateRow = (row) => {
  const unitPrice = roundMoney(row.unitPrice);
  const bookQuantity = parseQty(row.bookQuantity);
  const actualQuantity = parseQty(row.actualQuantity);
  const surplusQuantity = parseQty(row.surplusQuantity);
  const shortageQuantity = parseQty(row.shortageQuantity);
  const goodQuantity = parseQty(row.goodQuantity);
  const poorQuantity = parseQty(row.poorQuantity);
  const lostQuantity = parseQty(row.lostQuantity);

  const bookAmount = Math.round(unitPrice * bookQuantity);
  const actualAmount = Math.round(unitPrice * actualQuantity);
  const surplusAmount = Math.round(unitPrice * surplusQuantity);
  const shortageAmount = Math.round(unitPrice * shortageQuantity);

  return {
    ...row,
    unitPrice,
    bookQuantity,
    bookAmount,
    actualQuantity,
    actualAmount,
    surplusQuantity,
    surplusAmount,
    shortageQuantity,
    shortageAmount,
    goodQuantity,
    poorQuantity,
    lostQuantity,
  };
};

export default function InventorySummaryReportDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();

  const today = new Date();

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [inventoryDateParts, setInventoryDateParts] = useState({
    day: String(today.getDate()).padStart(2, "0"),
    month: String(today.getMonth() + 1).padStart(2, "0"),
    year: String(today.getFullYear()),
  });

  const [header, setHeader] = useState({
    unit: "",
    department: "",
    documentNumber: "",
    inventoryHour: "",
    note: "",
  });

  const [committeeMembers, setCommitteeMembers] = useState([
    createCommitteeMember("Trưởng ban"),
    createCommitteeMember("Ủy viên"),
    createCommitteeMember("Ủy viên"),
  ]);
  const [rows, setRows] = useState([createRow()]);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await InventorySummaryReportService.getById(id);
        const data = response.data ? response.data : response;

        setHeader({
          unit: data.unit || "",
          department: data.department || "",
          documentNumber: data.documentNumber || "",
          inventoryHour: data.inventoryHour || "",
          note: data.note || "",
        });

        setInventoryDateParts(splitDate(data.inventoryDate || ""));

        const mappedMembers = (data.committeeMembers || []).map((member) => ({
          fullName: member.fullName || "",
          position: member.position || "",
          representative: member.representative || "",
          role: member.role || "",
        }));
        if (mappedMembers.length) setCommitteeMembers(mappedMembers);

        const mappedRows = (data.rows || []).map((row) => ({
          itemName: row.itemName || "",
          itemCode: row.itemCode || "",
          unit: row.unit || "",
          unitPrice: formatVndIntegerInput(row.unitPrice || ""),
          bookQuantity: formatQtyOutput(row.bookQuantity),
          actualQuantity: formatQtyOutput(row.actualQuantity),
          surplusQuantity: formatQtyOutput(row.surplusQuantity),
          shortageQuantity: formatQtyOutput(row.shortageQuantity),
          goodQuantity: formatQtyOutput(row.goodQuantity),
          poorQuantity: formatQtyOutput(row.poorQuantity),
          lostQuantity: formatQtyOutput(row.lostQuantity),
        }));

        setRows(mappedRows.length ? mappedRows : [createRow()]);
      } catch (error) {
        toast.error("Không thể tải dữ liệu biên bản tổng hợp kiểm kê");
      }
    })();
  }, [id]);

  const derivedRows = useMemo(() => rows.map((row) => calculateRow(row)), [rows]);

  const totals = useMemo(() => {
    return derivedRows.reduce(
      (acc, row) => ({
        bookQuantity: acc.bookQuantity + row.bookQuantity,
        bookAmount: acc.bookAmount + row.bookAmount,
        actualQuantity: acc.actualQuantity + row.actualQuantity,
        actualAmount: acc.actualAmount + row.actualAmount,
        surplusQuantity: acc.surplusQuantity + row.surplusQuantity,
        surplusAmount: acc.surplusAmount + row.surplusAmount,
        shortageQuantity: acc.shortageQuantity + row.shortageQuantity,
        shortageAmount: acc.shortageAmount + row.shortageAmount,
        goodQuantity: acc.goodQuantity + row.goodQuantity,
        poorQuantity: acc.poorQuantity + row.poorQuantity,
        lostQuantity: acc.lostQuantity + row.lostQuantity,
      }),
      {
        bookQuantity: 0,
        bookAmount: 0,
        actualQuantity: 0,
        actualAmount: 0,
        surplusQuantity: 0,
        surplusAmount: 0,
        shortageQuantity: 0,
        shortageAmount: 0,
        goodQuantity: 0,
        poorQuantity: 0,
        lostQuantity: 0,
      }
    );
  }, [derivedRows]);

  const setHeaderField = (field, value) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
  };

  const setDateField = (field, value) => {
    const max = field === "year" ? 4 : 2;
    const raw = value.replace(/\D/g, "");
    setInventoryDateParts((prev) => ({ ...prev, [field]: field === "year" ? raw.slice(0, max) : raw.slice(-max) }));
  };

  const padDateField = (field) => {
    if (field === "year") return;
    setInventoryDateParts((prev) => {
      if (!prev[field]) return prev;
      return { ...prev, [field]: String(prev[field]).padStart(2, "0") };
    });
  };

  const setCommitteeField = (index, field, value) => {
    setCommitteeMembers((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addCommitteeMember = () => {
    setCommitteeMembers((prev) => [...prev, createCommitteeMember("Ủy viên")]);
  };

  const removeCommitteeMember = (index) => {
    setCommitteeMembers((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, idx) => idx !== index);
    });
  };

  const setRowField = (index, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      let nextValue = value;
      if (["unitPrice"].includes(field)) {
        nextValue = formatVndIntegerInput(value);
      } else if (
        [
          "bookQuantity",
          "actualQuantity",
          "surplusQuantity",
          "shortageQuantity",
          "goodQuantity",
          "poorQuantity",
          "lostQuantity",
        ].includes(field)
      ) {
        nextValue = formatQtyInput(value);
      }
      next[index] = { ...next[index], [field]: nextValue };
      return next;
    });
  };

  const addRow = () => {
    setRows((prev) => [...prev, createRow()]);
  };

  const removeRow = (index) => {
    setRows((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const buildPayload = () => {
    const unit = String(header.unit || "").trim();
    const department = String(header.department || "").trim();
    const documentNumber = String(header.documentNumber || "").trim();
    const inventoryHour = String(header.inventoryHour || "").trim();
    const note = String(header.note || "").trim();

    const inventoryDate = validateDate(inventoryDateParts, "Ngày kiểm kê");

    if (!unit) throw new Error("Đơn vị không được để trống");
    if (!department) throw new Error("Bộ phận không được để trống");
    if (!documentNumber) throw new Error("Số biên bản không được để trống");
    if (!inventoryHour) throw new Error("Giờ kiểm kê không được để trống");

    const normalizedMembers = committeeMembers
      .map((member) => ({
        fullName: String(member.fullName || "").trim(),
        position: String(member.position || "").trim(),
        representative: String(member.representative || "").trim(),
        role: String(member.role || "").trim(),
      }))
      .filter((member) => member.fullName || member.position || member.representative || member.role);

    if (!normalizedMembers.length) {
      throw new Error("Ban kiểm kê phải có ít nhất 1 thành viên");
    }

    normalizedMembers.forEach((member, index) => {
      if (!member.fullName) throw new Error(`Thành viên ban kiểm kê ${index + 1}: Họ tên không được để trống`);
      if (!member.role) throw new Error(`Thành viên ban kiểm kê ${index + 1}: Vai trò không được để trống`);
    });

    const normalizedRows = derivedRows
      .map((row, index) => ({
        serialNumber: String(index + 1),
        itemName: String(row.itemName || "").trim(),
        itemCode: String(row.itemCode || "").trim(),
        unit: String(row.unit || "").trim(),
        unitPrice: row.unitPrice,
        bookQuantity: row.bookQuantity,
        bookAmount: row.bookAmount,
        actualQuantity: row.actualQuantity,
        actualAmount: row.actualAmount,
        surplusQuantity: row.surplusQuantity,
        surplusAmount: row.surplusAmount,
        shortageQuantity: row.shortageQuantity,
        shortageAmount: row.shortageAmount,
        goodQuantity: row.goodQuantity,
        poorQuantity: row.poorQuantity,
        lostQuantity: row.lostQuantity,
      }))
      .filter((row) => {
        const hasQty =
          row.bookQuantity > 0 ||
          row.actualQuantity > 0 ||
          row.surplusQuantity > 0 ||
          row.shortageQuantity > 0 ||
          row.goodQuantity > 0 ||
          row.poorQuantity > 0 ||
          row.lostQuantity > 0;
        return row.itemName || row.itemCode || row.unit || hasQty;
      });

    if (!normalizedRows.length) {
      throw new Error("Biên bản tổng hợp kiểm kê phải có ít nhất 1 dòng dữ liệu");
    }

    normalizedRows.forEach((row, index) => {
      if (!row.itemName) throw new Error(`Dòng ${index + 1}: Tên, nhãn hiệu, quy cách không được để trống`);
    });

    return {
      unit,
      department,
      documentNumber,
      inventoryHour,
      inventoryDate,
      committeeMembers: normalizedMembers,
      note,
      rows: normalizedRows,
    };
  };

  const submit = async () => {
    try {
      setLoadingSubmit(true);
      const payload = buildPayload();
      if (!id) {
        await InventorySummaryReportService.create(payload);
        toast.success("Tạo biên bản tổng hợp kiểm kê thành công");
      } else {
        await InventorySummaryReportService.update(id, payload);
        toast.success("Cập nhật biên bản tổng hợp kiểm kê thành công");
      }
      navigation("/templates/inventory-summary-report");
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Lưu thất bại");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handlePrint = () => {
    const htmlContent = getPrintTemplate({
      headerInfo: {
        ...header,
        inventoryDate: composeDate(inventoryDateParts),
      },
      committeeMembers,
      rows: derivedRows.map((row, index) => ({ ...row, serialNumber: String(index + 1) })),
      totals,
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const s = {
    container: {
      fontFamily: '"Times New Roman", Times, serif',
      color: "#000",
      padding: "20px",
      backgroundColor: "#fff",
      fontSize: "16px",
    },
    inputDotted: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "16px",
      fontFamily: "inherit",
      background: "transparent",
      color: "#000",
      minWidth: 20,
    },
    inputDate: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "16px",
      fontFamily: "inherit",
      background: "transparent",
      color: "#000",
      width: 36,
      textAlign: "center",
    },
    table: { width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 1250 },
    th: { border: "1px solid #000", padding: 4, textAlign: "center", fontWeight: "bold", verticalAlign: "middle" },
    td: { border: "1px solid #000", padding: 0, height: 34 },
    inputLeft: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "left",
      fontSize: 13,
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
    inputCenter: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "center",
      fontSize: 13,
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
    inputRight: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "right",
      fontSize: 13,
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={10}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Card>
              <SoftBox p={2} style={s.container}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <div style={{ lineHeight: 1.5 }}>
                    <div>
                      <b>Đơn vị:</b>{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 220 }}
                        value={header.unit}
                        onChange={(event) => setHeaderField("unit", event.target.value)}
                      />
                    </div>
                    <div>
                      <b>Bộ phận:</b>{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 220 }}
                        value={header.department}
                        onChange={(event) => setHeaderField("department", event.target.value)}
                      />
                    </div>
                  </div>

                  <div style={{ textAlign: "center", fontSize: 15, lineHeight: 1.35 }}>
                    <div>
                      <b>Mẫu số: 05 - VT</b>
                    </div>
                    <div>
                      <i>(Kèm theo Thông tư số 99/2025/TT-BTC</i>
                    </div>
                    <div>
                      <i>ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)</i>
                    </div>
                  </div>
                </div>

                <div style={{ textAlign: "center", marginTop: 6 }}>
                  <div style={{ fontWeight: 700, fontSize: 26, textTransform: "uppercase" }}>
                    BIÊN BẢN TỔNG HỢP KIỂM KÊ VẬT TƯ, CÔNG CỤ, SẢN PHẨM, HÀNG HÓA
                  </div>
                </div>

                <div style={{ textAlign: "right", marginTop: 6 }}>
                  Số:{" "}
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 120, textAlign: "center" }}
                    value={header.documentNumber}
                    onChange={(event) => setHeaderField("documentNumber", event.target.value)}
                  />
                </div>

                <div style={{ marginTop: 6 }}>
                  - Thời điểm kiểm kê{" "}
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 60, textAlign: "center" }}
                    value={header.inventoryHour}
                    onChange={(event) => setHeaderField("inventoryHour", event.target.value)}
                  />{" "}
                  giờ ngày{" "}
                  <input
                    disabled={containView}
                    style={s.inputDate}
                    value={inventoryDateParts.day}
                    onChange={(event) => setDateField("day", event.target.value)}
                    onBlur={() => padDateField("day")}
                  />{" "}
                  tháng{" "}
                  <input
                    disabled={containView}
                    style={s.inputDate}
                    value={inventoryDateParts.month}
                    onChange={(event) => setDateField("month", event.target.value)}
                    onBlur={() => padDateField("month")}
                  />{" "}
                  năm{" "}
                  <input
                    disabled={containView}
                    style={{ ...s.inputDate, width: 56 }}
                    value={inventoryDateParts.year}
                    onChange={(event) => setDateField("year", event.target.value)}
                  />
                </div>

                <div style={{ marginTop: 6 }}>
                  - Ban kiểm kê/Hội đồng kiểm kê gồm:
                </div>

                {committeeMembers.map((member, index) => (
                  <div key={`committee-member-${index}`} style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 2 }}>
                    <span>+ Ông/Bà</span>
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 180 }}
                      value={member.fullName}
                      onChange={(event) => setCommitteeField(index, "fullName", event.target.value)}
                    />
                    <span>Chức vụ</span>
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 140 }}
                      value={member.position}
                      onChange={(event) => setCommitteeField(index, "position", event.target.value)}
                    />
                    <span>Đại diện</span>
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 160 }}
                      value={member.representative}
                      onChange={(event) => setCommitteeField(index, "representative", event.target.value)}
                    />
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 110 }}
                      value={member.role}
                      onChange={(event) => setCommitteeField(index, "role", event.target.value)}
                    />
                    {!containView ? (
                      <button
                        type="button"
                        onClick={() => removeCommitteeMember(index)}
                        style={{ border: "none", background: "transparent", color: "#d32f2f", cursor: "pointer" }}
                      >
                        x
                      </button>
                    ) : null}
                  </div>
                ))}

                {!containView ? (
                  <SoftButton size="small" variant="outlined" color="dark" onClick={addCommitteeMember} sx={{ mt: 1 }}>
                    Thêm thành viên
                  </SoftButton>
                ) : null}

                <div style={{ marginTop: 8 }}>
                  - Đã kiểm kê kho có những mặt hàng dưới đây:
                </div>

                <div style={{ overflowX: "auto", border: "1px solid #ccc", marginTop: 6 }}>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th rowSpan={3} style={{ ...s.th, width: 60 }}>STT</th>
                        <th rowSpan={3} style={{ ...s.th, width: 260 }}>Tên, nhãn hiệu, quy cách vật tư, dụng cụ,...</th>
                        <th rowSpan={3} style={{ ...s.th, width: 110 }}>Mã số</th>
                        <th rowSpan={3} style={{ ...s.th, width: 90 }}>Đơn vị tính</th>
                        <th rowSpan={3} style={{ ...s.th, width: 120 }}>Đơn giá</th>
                        <th colSpan={2} style={s.th}>Theo sổ kế toán</th>
                        <th colSpan={2} style={s.th}>Theo kiểm kê</th>
                        <th colSpan={4} style={s.th}>Chênh lệch</th>
                        <th colSpan={3} style={s.th}>Phẩm chất</th>
                        {!containView ? <th rowSpan={3} style={{ ...s.th, width: 40 }}>#</th> : null}
                      </tr>
                      <tr>
                        <th rowSpan={2} style={s.th}>Số lượng</th>
                        <th rowSpan={2} style={s.th}>Thành tiền</th>
                        <th rowSpan={2} style={s.th}>Số lượng</th>
                        <th rowSpan={2} style={s.th}>Thành tiền</th>
                        <th colSpan={2} style={s.th}>Thừa</th>
                        <th colSpan={2} style={s.th}>Thiếu</th>
                        <th rowSpan={2} style={s.th}>Còn tốt 100%</th>
                        <th rowSpan={2} style={s.th}>Kém phẩm chất</th>
                        <th rowSpan={2} style={s.th}>Mất phẩm chất</th>
                      </tr>
                      <tr>
                        <th style={s.th}>Số lượng</th>
                        <th style={s.th}>Thành tiền</th>
                        <th style={s.th}>Số lượng</th>
                        <th style={s.th}>Thành tiền</th>
                      </tr>
                      <tr>
                        <th style={s.th}>A</th>
                        <th style={s.th}>B</th>
                        <th style={s.th}>C</th>
                        <th style={s.th}>D</th>
                        <th style={s.th}>1</th>
                        <th style={s.th}>2</th>
                        <th style={s.th}>3</th>
                        <th style={s.th}>4</th>
                        <th style={s.th}>5</th>
                        <th style={s.th}>6</th>
                        <th style={s.th}>7</th>
                        <th style={s.th}>8</th>
                        <th style={s.th}>9</th>
                        <th style={s.th}>10</th>
                        <th style={s.th}>11</th>
                        <th style={s.th}>12</th>
                        {!containView ? <th style={s.th}>#</th> : null}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, index) => {
                        const calcRow = derivedRows[index];
                        return (
                          <tr key={`inventory-summary-row-${index}`}>
                            <td style={{ ...s.td, textAlign: "center", fontWeight: 600 }}>{index + 1}</td>
                            <td style={s.td}>
                              <input
                                disabled={containView}
                                style={s.inputLeft}
                                value={row.itemName}
                                onChange={(event) => setRowField(index, "itemName", event.target.value)}
                              />
                            </td>
                            <td style={s.td}>
                              <input
                                disabled={containView}
                                style={s.inputCenter}
                                value={row.itemCode}
                                onChange={(event) => setRowField(index, "itemCode", event.target.value)}
                              />
                            </td>
                            <td style={s.td}>
                              <input
                                disabled={containView}
                                style={s.inputCenter}
                                value={row.unit}
                                onChange={(event) => setRowField(index, "unit", event.target.value)}
                              />
                            </td>
                            <td style={s.td}>
                              <input
                                disabled={containView}
                                style={s.inputRight}
                                value={row.unitPrice}
                                onChange={(event) => setRowField(index, "unitPrice", event.target.value)}
                              />
                            </td>
                            <td style={s.td}>
                              <input
                                disabled={containView}
                                style={s.inputRight}
                                value={row.bookQuantity}
                                onChange={(event) => setRowField(index, "bookQuantity", event.target.value)}
                              />
                            </td>
                            <td style={{ ...s.td, textAlign: "right", padding: "0 6px" }}>{fmtMoney(calcRow.bookAmount)}</td>
                            <td style={s.td}>
                              <input
                                disabled={containView}
                                style={s.inputRight}
                                value={row.actualQuantity}
                                onChange={(event) => setRowField(index, "actualQuantity", event.target.value)}
                              />
                            </td>
                            <td style={{ ...s.td, textAlign: "right", padding: "0 6px" }}>{fmtMoney(calcRow.actualAmount)}</td>
                            <td style={s.td}>
                              <input
                                disabled={containView}
                                style={s.inputRight}
                                value={row.surplusQuantity}
                                onChange={(event) => setRowField(index, "surplusQuantity", event.target.value)}
                              />
                            </td>
                            <td style={{ ...s.td, textAlign: "right", padding: "0 6px" }}>{fmtMoney(calcRow.surplusAmount)}</td>
                            <td style={s.td}>
                              <input
                                disabled={containView}
                                style={s.inputRight}
                                value={row.shortageQuantity}
                                onChange={(event) => setRowField(index, "shortageQuantity", event.target.value)}
                              />
                            </td>
                            <td style={{ ...s.td, textAlign: "right", padding: "0 6px" }}>{fmtMoney(calcRow.shortageAmount)}</td>
                            <td style={s.td}>
                              <input
                                disabled={containView}
                                style={s.inputRight}
                                value={row.goodQuantity}
                                onChange={(event) => setRowField(index, "goodQuantity", event.target.value)}
                              />
                            </td>
                            <td style={s.td}>
                              <input
                                disabled={containView}
                                style={s.inputRight}
                                value={row.poorQuantity}
                                onChange={(event) => setRowField(index, "poorQuantity", event.target.value)}
                              />
                            </td>
                            <td style={s.td}>
                              <input
                                disabled={containView}
                                style={s.inputRight}
                                value={row.lostQuantity}
                                onChange={(event) => setRowField(index, "lostQuantity", event.target.value)}
                              />
                            </td>
                            {!containView ? (
                              <td style={{ ...s.td, textAlign: "center" }}>
                                <button
                                  type="button"
                                  onClick={() => removeRow(index)}
                                  style={{ border: "none", background: "transparent", color: "#d32f2f", fontWeight: 700, cursor: "pointer" }}
                                >
                                  x
                                </button>
                              </td>
                            ) : null}
                          </tr>
                        );
                      })}

                      <tr>
                        <td colSpan={5} style={{ ...s.td, textAlign: "center", fontWeight: 700 }}>Cộng</td>
                        <td style={{ ...s.td, textAlign: "right", padding: "0 6px", fontWeight: 700 }}>{formatQtyOutput(totals.bookQuantity)}</td>
                        <td style={{ ...s.td, textAlign: "right", padding: "0 6px", fontWeight: 700 }}>{fmtMoney(totals.bookAmount)}</td>
                        <td style={{ ...s.td, textAlign: "right", padding: "0 6px", fontWeight: 700 }}>{formatQtyOutput(totals.actualQuantity)}</td>
                        <td style={{ ...s.td, textAlign: "right", padding: "0 6px", fontWeight: 700 }}>{fmtMoney(totals.actualAmount)}</td>
                        <td style={{ ...s.td, textAlign: "right", padding: "0 6px", fontWeight: 700 }}>{formatQtyOutput(totals.surplusQuantity)}</td>
                        <td style={{ ...s.td, textAlign: "right", padding: "0 6px", fontWeight: 700 }}>{fmtMoney(totals.surplusAmount)}</td>
                        <td style={{ ...s.td, textAlign: "right", padding: "0 6px", fontWeight: 700 }}>{formatQtyOutput(totals.shortageQuantity)}</td>
                        <td style={{ ...s.td, textAlign: "right", padding: "0 6px", fontWeight: 700 }}>{fmtMoney(totals.shortageAmount)}</td>
                        <td style={{ ...s.td, textAlign: "right", padding: "0 6px", fontWeight: 700 }}>{formatQtyOutput(totals.goodQuantity)}</td>
                        <td style={{ ...s.td, textAlign: "right", padding: "0 6px", fontWeight: 700 }}>{formatQtyOutput(totals.poorQuantity)}</td>
                        <td style={{ ...s.td, textAlign: "right", padding: "0 6px", fontWeight: 700 }}>{formatQtyOutput(totals.lostQuantity)}</td>
                        {!containView ? <td style={s.td} /> : null}
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div style={{ marginTop: 8 }}>
                  <b>Ghi chú:</b>{" "}
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 420 }}
                    value={header.note}
                    onChange={(event) => setHeaderField("note", event.target.value)}
                  />
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
                  <SoftButton variant="outlined" color="dark" onClick={() => navigation(-1)}>
                    Trở về
                  </SoftButton>

                  <div style={{ display: "flex", gap: 12 }}>
                    {!containView ? (
                      <SoftButton variant="outlined" color="dark" onClick={addRow}>
                        THÊM DÒNG
                      </SoftButton>
                    ) : null}

                    {containView ? (
                      <SoftButton variant="gradient" color="success" onClick={handlePrint}>
                        In Bảng
                      </SoftButton>
                    ) : null}

                    {!containView ? (
                      <SoftButton variant="gradient" color="dark" onClick={submit} disabled={loadingSubmit}>
                        {id ? "Cập nhật" : "Tạo"}
                      </SoftButton>
                    ) : null}
                  </div>
                </div>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}
